// LangSwitcher — interface language selector. Migrated 2026-06-03 from
// a bespoke `dropdown.update({ style: {...} })` toggle to the
// @symbo.ls/dropdown plugin so positioning + edge-flip + outside-click
// + ESC dismiss are all owned by the shared DropdownRoot.
//
// The list comes from `functions/languages.js` — the platform's canonical
// language registry — not from a local copy. The four codes that used to be
// hardcoded here (en/de/fr/es) included three whose `i18n/<lang>/*.json`
// bundles were empty `{}`: picking one silently kept the whole UI in English,
// which reads as a broken translation rather than an absent language. Adding a
// language is now ONE edit to `PLATFORM_LANGUAGES` plus its bundles.
import { PLATFORM_LANGUAGES } from '../functions/languages.js'

export const LangSwitcherItem = {
  // House menu row (MenuPanelItem) so it inherits the shared visible :hover
  // wash + chrome — reuse the dropdown recipe instead of a bespoke Button
  // (which had no :hover at all).
  extends: 'MenuPanelItem',
  flow: 'x',
  gap: 'Z1',

  Code: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'caption',
    width: 'B',
    text: (el, s) => (s.code || '').toUpperCase()
  },
  Label: {
    tag: 'span',
    fontSize: 'Z2',
    // The ENDONYM — a language is named in its own language, never translated
    // into the current one ("Deutsch", not "German"). Georgian has no letter
    // case, so no casing is applied to it anywhere.
    //
    // Width: Georgian sets 12–17% longer than English, and `ქართული` is the
    // longest label the registry can produce today. MenuPanelItem carries
    // `whiteSpace: 'nowrap'`, so the row cannot wrap — but the panel below
    // sizes on content (`minWidth: 'G'` ≈ 6 steps up the spacing sequence,
    // `maxWidth: '100%'`), which is several times the ~95px the widest
    // Code+gap+Label row needs. No clipping, no restyle required.
    text: (el, s) => s.endonym || s.code || ''
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('closeDropdown', 'langSwitcher')
    // `el.call` binds `this` to the element inside `setLang`, which is how it
    // reaches `context.polyglot`, the root state and the SDK. It used to be an
    // arrow function, which cannot take that binding — the click changed
    // `<html lang>` and localStorage and nothing else.
    el.call('setLang', s.code)
  }
}

// Body-only menu the dropdown plugin renders inside its single
// shell-mounted DropdownRoot panel. House MenuPanel chassis (§7.7,
// bespoke-menus wave) — the plugin panel is chromeless (bodies own their
// card), so MenuPanel brings the dialog surface, overlay elevation, Y
// padding and role=menu; the hand-rolled div shell (Z padding + role) is
// retired. Rows were already MenuPanelItem.
export const LangSwitcherMenu = {
  extends: 'MenuPanel',
  minWidth: 'G',
  textAlign: 'left',

  childExtends: 'LangSwitcherItem',
  childrenAs: 'state',
  // The registry rows verbatim ({ code, endonym, english, dir }) — no derived
  // copy, so the switcher can never drift from what the platform ships.
  children: PLATFORM_LANGUAGES
}

export const LangSwitcher = {
  position: 'relative',
  flow: 'y',
  gap: 'Y',

  // THE lock test, carried on `scope` so no serialized handler reads a bare
  // module-scope binding (FA204 — the same reason `LanguageSelectField` carries
  // `endonym` here rather than importing it inside a prop). The resolution is
  // INLINED rather than calling `functions/languages.js`'s `resolveLang`: that
  // file is a frank section barrel and a cross-barrel read from inside a
  // serialized prop is unresolvable in the eval-revived runtime. Pinned to
  // `setLang`'s own gate by __tests__/i18nBootLanguage.test.js, which fails on
  // any root where this lock and that refusal disagree.
  scope: {
    SUPPORTED: ['en', 'ka'],
    // The ONE language `setLang` accepts inside the active workspace, or null
    // when it accepts any (no active workspace — a standalone or public
    // surface).
    //
    // THE LAW (Nika, 2026-09-10): inside a workspace its RESOLVED language
    // rules — enforced pair → `settings.language` → the white-label brand →
    // English — under 'default' and 'enforced' alike. `applyWorkspaceLangPolicy`
    // re-applies that on every active-workspace event, so an unlocked switcher
    // here offered a switch that painted and then snapped back. A record still
    // IN FLIGHT (an id, no `settings`, not stamped full) holds the active
    // language, exactly as the policy and `setLang` hold it.
    lockedLang(root) {
      const ws =
        root && root.workspace && typeof root.workspace === 'object' ? root.workspace : null
      if (!ws) return null
      const list = this.SUPPORTED || []
      const ok = (c) => typeof c === 'string' && list.indexOf(c) !== -1
      const settings = ws.settings && typeof ws.settings === 'object' ? ws.settings : null
      const pending =
        !settings && ws._shape !== 'full' && !!(ws.id || ws._id) && !ws._completionUnreachable
      if (pending) return ok(root.lang) ? root.lang : 'en'
      const brand = root.whitelabelBrand && root.whitelabelBrand.lang
      return (
        (settings && settings.languagePolicy === 'enforced' && ok(settings.language)
          ? settings.language
          : null) ||
        (settings && ok(settings.language) ? settings.language : null) ||
        (ok(brand) ? brand : null) ||
        'en'
      )
    }
  },

  Trigger: {
    extends: 'Button',
    padding: 'Z1 A',
    round: 'radiusControl',
    // A locked switcher is not a control. `disabled` is what says so to the
    // keyboard and to assistive tech, and the primitive owns the `:disabled`
    // look (Rule 65) — nothing about the state is restyled here.
    disabled: (el, s) => !!el.scope.lockedLang(s.root),
    cursor: (el, s) => (el.scope.lockedLang(s.root) ? 'default' : 'pointer'),
    theme: 'tertiary',
    // Rule 48 — no hardcoded user-facing string, including the accessible
    // name. The function form (not `{{ … | polyglot }}`) is used because these
    // are ATTRIBUTES: a template resolves ONCE, in the static attribute pass,
    // and would print its own braces into a tooltip. `polyglot` always returns
    // a string, and a reactive prop re-evaluates when the root state's `lang`
    // changes, so the label follows the switch.
    //
    // Under a lock the name says the workspace owns the language, rather than
    // offering a change that will not happen — a control announced as "Change
    // language" and then refusing is worse than one that says why.
    title: (el, s) =>
      el.call(
        'polyglot',
        el.scope.lockedLang(s.root) ? 'common.language.lockedByWorkspace' : 'common.language.change'
      ),
    'aria-label': (el, s) =>
      el.call(
        'polyglot',
        el.scope.lockedLang(s.root) ? 'common.language.lockedByWorkspace' : 'common.language.change'
      ),
    'aria-haspopup': (el, s) => (el.scope.lockedLang(s.root) ? null : 'menu'),

    Code: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      // The two-letter CODE, always Latin — `.toUpperCase()` is a no-op on
      // Georgian text but the code itself is `ka`, so the trigger reads `KA`.
      //
      // Under a lock this shows the ENFORCED code, not `root.lang`. They agree
      // once the policy has been applied, but this prop is also what paints
      // during the window before it — and a trigger that briefly advertises a
      // language the workspace refuses is the one thing a locked control must
      // never do.
      //
      // FA515 — `lang` is read UNCONDITIONALLY, into a local, BEFORE the lock
      // is consulted. On the right of a `||` a locked run short-circuits past
      // the signal and never tracks it, so leaving the enforced workspace
      // would leave this trigger frozen on the enforced code.
      text: (el, s) => {
        const active = s.root?.lang
        return (el.scope.lockedLang(s.root) || active || 'en').toUpperCase()
      }
    },

    onClick: (e, el, s) => {
      e.stopPropagation()
      // Layer 2 of the lock. `disabled` above already stops the pointer and the
      // keyboard; this stops everything else that can reach a handler, and it
      // is why the menu's own items need no guard of their own — they are
      // never mounted. `setLang` refuses independently (layer 3), which is what
      // covers the cross-tab echo that reaches no control at all.
      if (el.scope.lockedLang(s.root)) return
      el.call('openDropdown', {
        anchorEl: el,
        id: 'langSwitcher',
        placement: 'bottom-end',
        // ORGSWITCHER-PANEL-SURFACE-1 sweep: the width lives ONCE, on the body
        // (`LangSwitcherMenu.minWidth: 'G'`). The raw `minWidth: '200px'` that
        // used to sit here pinned the plugin shell to a px value that cannot
        // track an em body — the same shape that made the org switcher paint a
        // strip of the shell's own surface down its right edge. `round` is
        // handed through so the shell matches the body's `radiusCard`.
        round: 'radiusCard',
        content: { extends: 'LangSwitcherMenu' }
      })
    }
  },

  // The one line that explains the lock. A disabled control with no reason
  // beside it reads as a bug, not as a policy.
  //
  // `if:` (not `hide:`) so the line does not exist at all where nothing locks
  // (no active workspace) — and it is the LAST child,
  // so the gate resolving after the workspace record hydrates moves nothing
  // already on screen (Rule 66). The same placement the admin card's own
  // `if:`-gated section uses, for the same reason.
  Caption: {
    tag: 'span',
    if: (el, s) => !!el.scope.lockedLang(s.root),
    fontSize: 'Y2',
    color: 'caption',
    // The FUNCTION form, not `{{ … | polyglot }}`. Nothing on the account
    // path carries a polyglot TEMPLATE (pinned by LanguageSelectField.test.js)
    // — canvas serves this same page at its own /settings, and the function
    // form is the one that registers its own effect and cannot be frozen by
    // the static attribute pass.
    text: (el) => el.call('polyglot', 'common.language.setByWorkspace')
  }
}
